import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { Badge } from '../atoms/Badge/Badge';
import { Button } from '../atoms/Button/Button';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from '../../i18n/useTranslation';
import { REEF_GUARD_FINDINGS_KEY, listReefGuardFindings } from '../../services/reefGuardService';
import './AlertsHeaderButton.css';

/**
 * Header bell to the Alerts page with the number of open (unacknowledged) Reef Guard
 * findings across the user's aquariums — open notices were only visible once the
 * user went looking for them (usability audit §4.4). The count is in the button's
 * name too, never only in the badge.
 */
export function AlertsHeaderButton() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const findings = useQuery({
    queryKey: REEF_GUARD_FINDINGS_KEY,
    queryFn: listReefGuardFindings,
    enabled: isAuthenticated,
    staleTime: 5 * 60_000,
  });

  if (!isAuthenticated) return null;

  const open = (findings.data ?? []).filter((f) => !f.acknowledged).length;

  return (
    <Button
      variant="ghost"
      className="alerts-header-button"
      aria-label={open > 0 ? t('reefGuard.headerButton.labelOpen', { count: open }) : t('reefGuard.headerButton.label')}
      onClick={() => navigate('/alerts')}
      data-testid="alerts-header-button"
    >
      <Bell size={20} aria-hidden="true" />
      {open > 0 && (
        <Badge tone="warning" className="alerts-header-button__count" aria-hidden="true" data-testid="alerts-header-count">
          {open > 9 ? '9+' : open}
        </Badge>
      )}
    </Button>
  );
}
